if (!customElements.get('featured-collection')) {
  customElements.define('featured-collection', class FeaturedCollection extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.selectors = {sliderWrapper: '.featured-collection__items', nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev'};
      this.classes = {grid: 'f-grid', swiper: 'swiper', swiperWrapper: 'swiper-wrapper'};
      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
      if (!this.section || !this.sliderWrapper) return;
      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items);
      this.tabletItems = parseInt(this.dataset.tabletItems);
      this.isCartUpsell = this.section.classList.contains('pp-cart-recommendations');
      this.sliderInstance = false;
      if (this.isCartUpsell) {
        this.initCartScrolling();
        return;
      }
      if (!this.enableSlider) return;
      this.mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
      this.onMediaChange = this.init.bind(this);
      this.mql.addEventListener('change', this.onMediaChange);
      window.addEventListener('resize', FoxTheme.utils.debounce(() => this.calcNavButtonsPosition(), 100), {signal: this.controller.signal});
      this.init();
    }

    // Cart recommendations use native scrolling at every width, with no card snap.
    initCartScrolling() {
      const signal = this.controller.signal;
      this.classList.remove('swiper');
      this.classList.add('nr-cart-native');
      this.sliderWrapper.classList.remove('swiper-wrapper', 'f-grid');
      this.section.style.setProperty('--nr-cart-columns-desktop', Math.max(1, this.items || 6));
      this.cartButtons = Array.from(this.section.querySelectorAll('.swiper-button-prev, .swiper-button-next, .nr-fc-mobile-arrow'));
      this.updateCartControls = () => {
        const overflow = this.scrollWidth > this.clientWidth + 2;
        const offset = Math.abs(this.scrollLeft);
        const maximum = this.scrollWidth - this.clientWidth;
        this.cartButtons.forEach(button => {
          const previous = button.matches('.swiper-button-prev, .nr-fc-mobile-prev');
          button.disabled = !overflow || (previous ? offset <= 2 : offset >= maximum - 2);
          button.setAttribute('aria-disabled', String(button.disabled));
          button.type = 'button';
        });
        const media = this.querySelector('.product-card__image-wrapper');
        const grid = this.closest('grid-list');
        if (media && grid) {
          const rect = media.getBoundingClientRect(), region = grid.getBoundingClientRect();
          const position = `${rect.top - region.top + rect.height / 2}px`;
          this.section.style.setProperty('--swiper-navigation-top-offset', position);
          this.section.querySelectorAll('.nr-fc-mobile-controls').forEach(el => el.style.setProperty('--swiper-navigation-top-offset', position));
        }
      };
      this.section.addEventListener('click', event => {
        const button = event.target.closest('.swiper-button-prev, .swiper-button-next, .nr-fc-mobile-arrow');
        if (!button || !this.section.contains(button)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (button.disabled) return;
        const card = this.sliderWrapper.firstElementChild;
        if (!card) return;
        const gap = parseFloat(getComputedStyle(this.sliderWrapper).columnGap) || 12;
        const direction = button.matches('.swiper-button-prev, .nr-fc-mobile-prev') ? -1 : 1;
        const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        const step = card.getBoundingClientRect().width + gap;
        const offset = Math.abs(this.scrollLeft);
        const targetIndex = direction > 0 ? Math.floor((offset + 1) / step) + 1 : Math.ceil((offset - 1) / step) - 1;
        const maximum = Math.max(0, this.scrollWidth - this.clientWidth);
        const destination = Math.max(0, Math.min(maximum, targetIndex * step));
        this.scrollTo({left: destination * rtl, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      }, {capture: true, signal});
      let drag = null, blockClick = false;
      const finish = () => {
        if (!drag) return;
        blockClick = drag.moved;
        if (this.hasPointerCapture(drag.id)) this.releasePointerCapture(drag.id);
        drag = null;
        this.classList.remove('is-dragging');
      };
      this.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button, input, select, textarea')) return;
        blockClick = false;
        drag = {id: event.pointerId, x: event.clientX, left: this.scrollLeft, moved: false};
      }, {signal});
      this.addEventListener('pointermove', event => {
        if (!drag || drag.id !== event.pointerId) return;
        if (!event.buttons) { finish(); return; }
        const delta = event.clientX - drag.x;
        if (!drag.moved && Math.abs(delta) < 5) return;
        drag.moved = true;
        this.setPointerCapture(event.pointerId);
        this.classList.add('is-dragging');
        this.scrollLeft = drag.left - delta;
        event.preventDefault();
      }, {signal});
      this.addEventListener('pointerup', finish, {signal});
      this.addEventListener('pointercancel', finish, {signal});
      this.addEventListener('lostpointercapture', finish, {signal});
      this.addEventListener('click', event => {
        if (!blockClick) return;
        event.preventDefault(); event.stopImmediatePropagation(); blockClick = false;
      }, {capture: true, signal});
      this.addEventListener('dragstart', event => event.preventDefault(), {signal});
      this.addEventListener('scroll', this.updateCartControls, {passive: true, signal});
      this.addEventListener('load', this.updateCartControls, {capture: true, signal});
      window.addEventListener('resize', this.updateCartControls, {signal});
      if (window.ResizeObserver) {
        this.cartResizeObserver = new ResizeObserver(this.updateCartControls);
        this.cartResizeObserver.observe(this);
      }
      requestAnimationFrame(this.updateCartControls);
      this.loadCartBestSellers();
    }

    async loadCartBestSellers() {
      const root = window.Shopify?.routes?.root || '/';
      const allUrl = `${root}collections/all?sort_by=best-selling`;
      const heading = this.section.querySelector('.section__heading');
      if (heading) {
        let link = heading.querySelector('a');
        if (!link) {
          link = document.createElement('a');
          while (heading.firstChild) link.appendChild(heading.firstChild);
          heading.appendChild(link);
        }
        link.href = allUrl;
      }
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${allUrl}&view=rf-cart-recommendations`, {
          signal: this.controller.signal, credentials: 'same-origin',
        });
        if (!response.ok) throw new Error('Could not load best sellers');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const sorted = parsed.querySelector('[data-rf-cart-best-sellers]');
        if (!sorted || !sorted.children.length) throw new Error('Best-seller template missing or empty');
        this.sliderWrapper.replaceChildren(...Array.from(sorted.children));
        this.scrollLeft = 0;
        this.updateCartControls();
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('[Cart recommendations]', error.message);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.mql?.removeEventListener('change', this.onMediaChange);
      this.cartResizeObserver?.disconnect();
      if (this.sliderInstance?.slider) this.sliderInstance.slider.destroy();
      this.sliderInstance = false;
    }
    init() {
      if (FoxTheme.config.mqlMobile) this.destroySlider();
      else this.initSlider();
    }
    initSlider() {
      if (typeof this.sliderInstance === 'object') return;
      const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);
      this.sliderOptions = {
        slidesPerView: this.tabletItems > 3 ? 3 : parseInt(this.tabletItems),
        spaceBetween: columnGap.tablet,
        navigation: {nextEl: this.section.querySelector(this.selectors.nextEl), prevEl: this.section.querySelector(this.selectors.prevEl)},
        pagination: false,
        breakpoints: {
          1024: {slidesPerView: this.items > 4 ? 4 : parseInt(this.items), spaceBetween: columnGap.tabletLarge},
          1280: {slidesPerView: parseInt(this.items), spaceBetween: columnGap.desktop},
        },
        loop: true, threshold: 2,
      };
      this.classList.add(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.grid);
      this.sliderWrapper.classList.add(this.classes.swiperWrapper);
      this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions);
      this.sliderInstance.init();
      this.fixQuickviewDuplicate();
      this.calcNavButtonsPosition();
    }
    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance !== 'object') return;
      this.sliderInstance.slider.destroy();
      this.sliderInstance = false;
    }
    calcNavButtonsPosition() {
      if (this.dataset.calcButtonPosition !== 'true') return;
      const media = this.querySelector('.product-card__image-wrapper');
      if (media && media.clientHeight > 0) this.section.style.setProperty('--swiper-navigation-top-offset', parseInt(media.clientHeight) / 2 + 'px');
    }
    calcHeadingColumnHeight() {
      if (this.dataset.calcHeadingColumnHeight !== 'true') return;
      const media = this.querySelector('.product-card__image-wrapper');
      if (media && media.clientHeight > 0) this.section.style.setProperty('--heading-column-height', media.clientHeight + 'px');
    }
    fixQuickviewDuplicate() {
      const ids = new Set();
      this.querySelectorAll('quick-view-modal').forEach(modal => {
        const id = modal.getAttribute('id');
        if (ids.has(id)) modal.remove(); else ids.add(id);
      });
    }
  });
}
